import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { fmtPrice, CITIES, withWatermark } from "@/lib/utils";
import {
  BedDouble, MapPin, CalendarDays, Sparkles, Search, Filter,
  Users, Building2, Home, Loader2, ArrowRight, Phone, Star, MessageCircle,
} from "lucide-react";
import EquipementsBadges, { EQUIPEMENTS_CATALOG } from "@/components/EquipementsBadges";

// Popular equipment filters shown as toggle chips in the search bar
const FILTER_EQUIPEMENTS = ["piscine", "parking", "wifi", "climatisation", "cuisine_equipee", "tv", "generateur", "securite"];

// Subcategories that qualify as "furnished rental" (Immobilier > Location)
const FURNISHED_SUBCATEGORIES = ["Maison meublee", "Studio", "Appartement"];

const HOUSING_TYPES = [
  { value: "", label: "Tous les logements", icon: BedDouble },
  { value: "Maison meublee", label: "Maison meublee", icon: Home },
  { value: "Studio", label: "Studio", icon: BedDouble },
  { value: "Appartement", label: "Appartement", icon: Building2 },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const inNDaysISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const nightsBetween = (a, b) => {
  if (!a || !b) return 0;
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
};

export default function LocationMeubleePage() {
  const [type, setType] = useState("");
  const [city, setCity] = useState("");
  const [arrival, setArrival] = useState(todayISO());
  const [departure, setDeparture] = useState(inNDaysISO(2));
  const [maxBudget, setMaxBudget] = useState("");
  const [equipements, setEquipements] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fallbackMode, setFallbackMode] = useState(false);

  // Two-pass fetch:
  //   1. Try strict "furnished-only" filter (subcategory + statut=location).
  //   2. If empty AND user didn't set explicit type/budget, show all Immobilier
  //      annonces so the page isn't empty just because sellers haven't yet
  //      tagged their listings with the right subcategory. Surface this
  //      relaxation via `fallbackMode` so the UI can explain it.
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setFallbackMode(false);
      try {
        const baseParams = new URLSearchParams();
        baseParams.set("category", "Immobilier");
        if (type) baseParams.set("subcategory", type);
        if (city) baseParams.set("city", city);
        if (maxBudget) baseParams.set("max_price", String(Number(maxBudget)));
        if (equipements.length > 0) baseParams.set("equipements", equipements.join(","));
        baseParams.set("page_size", "48");
        baseParams.set("sort", "recent");

        // Pass 1: strict filter (statut=location)
        const strictParams = new URLSearchParams(baseParams);
        strictParams.set("statut", "location");
        let res = await axios.get(`${API}/annonces?${strictParams.toString()}`, { signal: controller.signal });
        // API returns either a plain array or { items: [...], total }. Normalise.
        let raw = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        let filtered = type
          ? raw
          : raw.filter((a) => !a.subcategory || FURNISHED_SUBCATEGORIES.includes(a.subcategory));

        // Pass 2: fallback — no strict furnished result, widen to all Immobilier
        if (filtered.length === 0 && !type && !maxBudget && equipements.length === 0) {
          res = await axios.get(`${API}/annonces?${baseParams.toString()}`, { signal: controller.signal });
          const raw2 = Array.isArray(res.data) ? res.data : (res.data?.items || []);
          filtered = raw2;
          if (filtered.length > 0 && !cancelled) setFallbackMode(true);
        }

        if (!cancelled) setItems(filtered);
      } catch (e) {
        if (e.name !== "CanceledError" && e.name !== "AbortError" && !cancelled) {
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const t = setTimeout(load, 250);
    return () => { cancelled = true; controller.abort(); clearTimeout(t); };
  }, [type, city, maxBudget, equipements]);

  const toggleEquipement = (k) => {
    setEquipements((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));
  };

  const nights = useMemo(() => nightsBetween(arrival, departure), [arrival, departure]);
  const dateValid = nights > 0;

  const resultsLabel = useMemo(() => {
    if (loading) return "Recherche…";
    const n = items.length;
    if (n === 0) return "Aucun logement disponible pour ces criteres";
    if (n === 1) return "1 logement trouve";
    return `${n} logements disponibles`;
  }, [loading, items.length]);

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="location-meublee-page">
      {/* HERO + SEARCH */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-100 via-teal-50 to-white border border-emerald-200 px-6 py-8 md:px-10 md:py-12">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-emerald-300/30 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-teal-300/20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-white text-emerald-700 px-3 py-1 rounded-full border border-emerald-200">
            <Sparkles size={12} /> Nouveau · Reservation courte duree
          </span>
          <h1 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight mt-3 leading-tight">
            Location meublee<br className="hidden md:block" />
            <span className="text-emerald-700">reservez en 2 minutes.</span>
          </h1>
          <p className="text-[var(--ink-soft)] mt-3 max-w-2xl text-sm md:text-base">
            Studios, appartements et maisons meublees — cle en main pour vos sejours a Abidjan et dans toute la zone UEMOA.
          </p>

          {/* Search bar */}
          <div className="mt-6 bg-white/80 backdrop-blur rounded-2xl border border-emerald-200 shadow-sm p-3 md:p-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
              {/* Type */}
              <label className="block md:col-span-1">
                <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--ink-soft)] flex items-center gap-1"><BedDouble size={11} /> Type</span>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full mt-0.5 !py-2 rounded-xl border border-[var(--line)] bg-white text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 outline-none"
                  data-testid="location-meublee-type"
                >
                  {HOUSING_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </label>
              {/* Ville */}
              <label className="block md:col-span-1">
                <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--ink-soft)] flex items-center gap-1"><MapPin size={11} /> Ville</span>
                <select
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full mt-0.5 !py-2 rounded-xl border border-[var(--line)] bg-white text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 outline-none"
                  data-testid="location-meublee-city"
                >
                  <option value="">Toutes</option>
                  {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              {/* Arrivee */}
              <label className="block md:col-span-1">
                <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--ink-soft)] flex items-center gap-1"><CalendarDays size={11} /> Arrivee</span>
                <input
                  type="date"
                  value={arrival}
                  min={todayISO()}
                  onChange={(e) => setArrival(e.target.value)}
                  className="w-full mt-0.5 !py-2 rounded-xl border border-[var(--line)] bg-white text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 outline-none"
                  data-testid="location-meublee-arrival"
                />
              </label>
              {/* Depart */}
              <label className="block md:col-span-1">
                <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--ink-soft)] flex items-center gap-1"><CalendarDays size={11} /> Depart</span>
                <input
                  type="date"
                  value={departure}
                  min={arrival}
                  onChange={(e) => setDeparture(e.target.value)}
                  className="w-full mt-0.5 !py-2 rounded-xl border border-[var(--line)] bg-white text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 outline-none"
                  data-testid="location-meublee-departure"
                />
              </label>
              {/* Budget */}
              <label className="block md:col-span-1">
                <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--ink-soft)] flex items-center gap-1"><Filter size={11} /> Budget max/nuit</span>
                <div className="relative mt-0.5">
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    step="1000"
                    value={maxBudget}
                    onChange={(e) => setMaxBudget(e.target.value.replace(/\D/g, ""))}
                    placeholder="50 000"
                    className="w-full !py-2 !pr-12 rounded-xl border border-[var(--line)] bg-white text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 outline-none"
                    data-testid="location-meublee-budget"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--ink-soft)]">FCFA</span>
                </div>
              </label>
            </div>

            {/* Equipment filter chips */}
            <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="location-meublee-equipements">
              <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--ink-soft)] mr-1">Equipements :</span>
              {FILTER_EQUIPEMENTS.map((k) => {
                const { icon: Icon, label } = EQUIPEMENTS_CATALOG[k];
                const active = equipements.includes(k);
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => toggleEquipement(k)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-colors ${
                      active
                        ? "bg-emerald-600 border-emerald-600 text-white"
                        : "bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    }`}
                    data-testid={`equipement-filter-${k}`}
                    aria-pressed={active}
                  >
                    <Icon size={12} /> {label}
                  </button>
                );
              })}
              {equipements.length > 0 && (
                <button
                  type="button"
                  onClick={() => setEquipements([])}
                  className="text-[11px] font-semibold text-orange-600 hover:underline ml-1"
                  data-testid="equipements-clear"
                >
                  Effacer ({equipements.length})
                </button>
              )}
            </div>

            <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs text-[var(--ink-soft)]" data-testid="location-meublee-summary">
                {dateValid ? (
                  <>
                    <b className="text-emerald-700">{nights} nuit{nights > 1 ? "s" : ""}</b>
                    {" · "}
                    du {new Date(arrival).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                    {" au "}
                    {new Date(departure).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                  </>
                ) : (
                  <span className="text-orange-600">Selectionnez une date de depart posterieure a l&apos;arrivee.</span>
                )}
              </div>
              <div className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1">
                <Search size={12} /> Filtre automatique
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="mt-8">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
          <h2 className="font-display text-2xl md:text-3xl font-bold" data-testid="location-meublee-count">
            {resultsLabel}
          </h2>
          <Link to="/immobilier" className="text-sm font-semibold text-emerald-700 hover:underline inline-flex items-center gap-1">
            Voir tout l&apos;immobilier <ArrowRight size={14} />
          </Link>
        </div>

        {fallbackMode && (
          <div className="mb-4 p-3 rounded-2xl bg-amber-50 border border-amber-200 text-sm flex items-start gap-2" data-testid="location-meublee-fallback-notice">
            <Sparkles size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <b>Aucun logement strictement etiquete &quot;meuble&quot; pour l&apos;instant.</b> Voici
              toutes les annonces immobilieres disponibles — filtrez par type ci-dessus pour affiner.
            </div>
          </div>
        )}

        {loading ? (
          <div className="glass-card p-10 grid place-items-center">
            <Loader2 size={22} className="animate-spin text-emerald-600" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState onReset={() => { setType(""); setCity(""); setMaxBudget(""); setEquipements([]); }} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="location-meublee-grid">
            {items.map((a) => (
              <ListingCard key={a.id} a={a} nights={nights} arrival={arrival} departure={departure} />
            ))}
          </div>
        )}
      </section>

      {/* WHY US */}
      <section className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { icon: BedDouble, title: "Cle en main", desc: "Meuble, equipe et prêt a habiter des le premier jour." },
          { icon: Users, title: "Vendeur verifie", desc: "Numero de telephone valide + badge conformite sur les annonces Pro." },
          { icon: Star, title: "Prix transparents", desc: "Aucun frais cache. Le prix affiche est celui que vous payez au proprietaire." },
        ].map((v) => (
          <div key={v.title} className="glass-card p-5">
            <div className="w-10 h-10 rounded-2xl bg-emerald-100 grid place-items-center text-emerald-700 mb-2">
              <v.icon size={18} />
            </div>
            <div className="font-bold">{v.title}</div>
            <p className="text-sm text-[var(--ink-soft)] mt-1">{v.desc}</p>
          </div>
        ))}
      </section>
    </main>
  );
}

function ListingCard({ a, nights, arrival, departure }) {
  const img = a.image || (a.images && a.images[0]) || "";
  const total = nights > 0 ? a.price * nights : null;

  // Build a pre-filled WhatsApp message with the selected dates so the buyer
  // can hit "Réserver" and immediately open a conversation with the seller.
  // Falls back to the phone if the seller hasn't set a WhatsApp number.
  const waNumber = (a.whatsapp || a.phone || "").replace(/\D/g, "");
  const waEnabled = waNumber.length >= 8 && nights > 0;
  const buildWaLink = () => {
    const dateStr = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
    const msg =
      `Bonjour, je souhaite reserver votre logement "${a.title}" a ${a.city}` +
      (a.quartier ? ` (${a.quartier})` : "") +
      ` du ${dateStr(arrival)} au ${dateStr(departure)} (${nights} nuit${nights > 1 ? "s" : ""}).` +
      (total ? ` Budget estime : ${fmtPrice(total)}.` : "") +
      ` Reference : ${a.ref || "-"}. Merci !`;
    return `https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <article
      className="group bg-white rounded-2xl border border-[var(--line)] overflow-hidden hover:shadow-lg hover:border-emerald-300 transition-all flex flex-col"
      data-testid={`location-meublee-card-${a.id}`}
    >
      <Link to={`/annonces/${a.ref || a.id}`} className="block">
        <div className="relative aspect-[4/3] bg-gradient-to-br from-emerald-50 to-teal-50 overflow-hidden">
          {img ? (
            <img
              src={withWatermark(img)}
              alt={a.title}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full grid place-items-center text-emerald-300">
              <Home size={48} />
            </div>
          )}
          {a.subcategory && (
            <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide bg-white/95 backdrop-blur px-2 py-1 rounded-full border border-emerald-200 text-emerald-700">
              {a.subcategory}
            </span>
          )}
          {a.seller_verified && (
            <span className="absolute top-2 right-2 text-[10px] font-bold bg-emerald-600 text-white px-2 py-1 rounded-full inline-flex items-center gap-1">
              <Star size={10} fill="currentColor" /> Verifie
            </span>
          )}
        </div>
        <div className="p-4 pb-2">
          <h3 className="font-bold text-base leading-snug line-clamp-2 group-hover:text-emerald-700 transition">
            {a.title}
          </h3>
          <div className="text-xs text-[var(--ink-soft)] mt-1 inline-flex items-center gap-1">
            <MapPin size={12} /> {a.city}{a.quartier ? ` · ${a.quartier}` : ""}
          </div>
          <div className="mt-3">
            <div className="text-xs text-[var(--ink-soft)]">a partir de</div>
            <div className="font-bold text-emerald-700 text-lg leading-tight">{fmtPrice(a.price)}</div>
            {total && (
              <div className="text-[11px] text-[var(--ink-soft)]">
                ≈ {fmtPrice(total)} pour {nights} nuit{nights > 1 ? "s" : ""}
              </div>
            )}
            {a.equipements && a.equipements.length > 0 && (
              <EquipementsBadges keys={a.equipements} variant="compact" max={4} className="mt-2" />
            )}
          </div>
        </div>
      </Link>

      {/* Booking CTA — pre-filled WhatsApp with dates */}
      <div className="px-4 pb-4 pt-1 mt-auto flex gap-2">
        {waEnabled ? (
          <a
            href={buildWaLink()}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(e) => e.stopPropagation()}
            className="btn-green flex-1 !py-2 text-sm inline-flex items-center justify-center gap-1.5 font-semibold"
            data-testid={`reserve-wa-${a.id}`}
          >
            <MessageCircle size={14} /> Reserver via WhatsApp
          </a>
        ) : (
          <Link
            to={`/annonces/${a.ref || a.id}`}
            className="btn-secondary flex-1 !py-2 text-sm inline-flex items-center justify-center gap-1.5"
            data-testid={`reserve-fallback-${a.id}`}
          >
            <Phone size={14} /> {nights > 0 ? "Contacter" : "Selectionnez vos dates"}
          </Link>
        )}
      </div>
    </article>
  );
}

function EmptyState({ onReset }) {
  return (
    <div className="glass-card p-10 text-center" data-testid="location-meublee-empty">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 grid place-items-center text-emerald-600 mb-3">
        <BedDouble size={22} />
      </div>
      <div className="font-bold text-lg">Aucun logement pour ces criteres</div>
      <p className="text-sm text-[var(--ink-soft)] mt-1 max-w-md mx-auto">
        Elargissez vos filtres (ville, budget ou type) ou consultez toutes les annonces immobilieres.
      </p>
      <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
        <button onClick={onReset} className="btn-secondary !py-2 !px-4 text-sm" data-testid="location-meublee-reset">
          Reinitialiser les filtres
        </button>
        <Link to="/immobilier" className="btn-primary !py-2 !px-4 text-sm inline-flex items-center gap-1">
          Voir l&apos;immobilier <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
